import { CoreError, CoreErrorCode, ErrorInfo } from 'trtc-js-sdk-core';
import { RTCErrorParams, RTCErrorInterface } from '../type';
import { ErrorCode, ErrorCodeDictionary } from './error-code';
import { getErrorMessage } from './error-message';
import { IS_MAC, IS_WIN } from 'trtc-js-sdk-core/src/utils/environment';
/**
 * RtcError error object
 *
 * @extends Error
 * @class RtcError
 *
 */
class RtcError extends Error implements RTCErrorInterface {
  name = 'RtcError';
  /**
   *
   * Error code
   * @see Detailed error code list: {@link module:ERROR_CODE ErrorCode}
   * @readonly
   * @memberof RtcError
   */
  code: number;
  /**
   *
   * Extended error code
   * @see Detailed error code list: {@link module:ERROR_CODE ErrorCode}
   * @readonly
   * @memberof RtcError
   */
  extraCode?: number;
  /**
   *
   * The name of the function that throws the error
   * @readonly
   * @memberof RtcError
   */
  functionName: string;
  /**
   *
   * Error message
   * @readonly
   * @memberof RtcError
   */
  message: string;
  /**
   * Error handler. For the following errors, you can call error.handler() for recovering.
   *
   * - {@link module:ERROR_CODE.DEVICE_ERROR ErrorCode.DEVICE_ERROR} extraCode: 5302
   *   ```js
   *    trtc.startLocalAudio().catch(error => {
   *     if (error.extraCode === 5302 && typeof error.handler === 'function') {
   *       // Prompt the user the browser permission(camera/microphone/screen sharing) has been denied by system. The browser will jump to the System Settings APP, please enable the relevant permissions!
   *       // Available in Windows and MacOS.
   *       error.handler();
   *     }
   *    })
   *   ```
   * @since v5.2.0
   * @readonly
   * @memberof RtcError
   */
  data?: any;
  handler?: () => void;
  originError?: Error | DOMException | RtcError;

  constructor({ code, extraCode, message = '', messageParams, fnName = '', originError, data }: RTCErrorParams) {
    let _message: string;
    if (!message) {
      _message = getErrorMessage({
        // code: extraCode ? extraCode : code,
        code: code === ErrorCode.SERVER_ERROR ? code : extraCode || code,
        params: { fnName, error: originError, ...messageParams }
      });
    } else {
      _message = message;
    }
    super(_message);
    this.name = ErrorCodeDictionary[code];
    this.code = code;
    this.extraCode = extraCode;
    this.functionName = fnName;
    this.originError = originError;
    this.message = _message;
    this.data = data;
    if (this.extraCode === ErrorCodeDictionary.DEVICE_NOT_ALLOWED_ERROR && this.originError?.message.includes('system')) {
      this.handler = () => {
        const MAC_URL_SCHEMES = {
          startLocalVideo: 'Camera',
          startLocalAudio: 'Microphone',
          startScreenShare: 'ScreenCapture'
        };
        const WIN_URL_SCHEMES = {
          startLocalVideo: 'webcam',
          startLocalAudio: 'microphone'
        };
        const a = document.createElement('a');
        if (IS_WIN) {
          a.href = `ms-settings:privacy-${WIN_URL_SCHEMES[this.functionName as keyof typeof WIN_URL_SCHEMES]}`;
        } else if (IS_MAC) {
          a.href = `x-apple.systempreferences:com.apple.preference.security?Privacy_${MAC_URL_SCHEMES[this.functionName as keyof typeof WIN_URL_SCHEMES]}`;
        }
        if (a.href.length > 0) {
          a.click();
        }
      };
    }
  }
  static convertFrom(originError: Error, fnName?: string, fnParams?: any) {
    let err = originError;
    if (originError instanceof CoreError) {
      const { stack } = originError;
      const rtcErrorParams: RTCErrorParams = {
        code: ErrorCode.UNKNOWN_ERROR,
        fnName,
        originError
      };
      switch (originError.getCode()) {
        case CoreErrorCode.INVALID_PARAMETER:
          rtcErrorParams.code = ErrorCode.INVALID_PARAMETER;
          rtcErrorParams.message = originError.message;
          break;
        case CoreErrorCode.INVALID_OPERATION:
          rtcErrorParams.code = ErrorCode.INVALID_OPERATION;
          rtcErrorParams.message = originError.message;
          break;
        case CoreErrorCode.NOT_SUPPORTED:
        case CoreErrorCode.NOT_SUPPORTED_H264:
          rtcErrorParams.code = ErrorCode.ENV_NOT_SUPPORTED;
          if (originError.getCode() === CoreErrorCode.NOT_SUPPORTED_H264) {
            rtcErrorParams.extraCode = originError.message.includes(ErrorInfo.NOT_SUPPORTED_H264ENCODE) ? ErrorCodeDictionary.NOT_SUPPORTED_H264_ENCODE : ErrorCodeDictionary.NOT_SUPPORTED_H264_DECODE;
          }
          break;
        case CoreErrorCode.JOIN_ROOM_FAILED:
          rtcErrorParams.messageParams = { fnParams };
        case CoreErrorCode.SERVER_TIMEOUT:
        case CoreErrorCode.SWITCH_ROLE_FAILED:
        case CoreErrorCode.SWITCH_ROOM_FAILED:
          rtcErrorParams.code = ErrorCode.SERVER_ERROR;
          rtcErrorParams.extraCode = originError.getExtraCode();
          break;
        case CoreErrorCode.API_CALL_ABORTED:
          rtcErrorParams.code = ErrorCode.OPERATION_ABORT;
          break;
        case CoreErrorCode.DEVICE_NOT_FOUND:
        case CoreErrorCode.DEVICE_AUTO_RECOVER_FAILED:
        case CoreErrorCode.INITIALIZE_FAILED:
          rtcErrorParams.code = ErrorCodeDictionary.DEVICE_ERROR;
          if (originError.name) {
            rtcErrorParams.extraCode = convertDeviceError(originError.name);
          }
          break;
        case CoreErrorCode.VIDEO_ENCODE_FAILED:
          rtcErrorParams.extraCode = ErrorCodeDictionary.VIDEO_ENCODE_FAILED;
        case CoreErrorCode.AUDIO_ENCODE_FAILED:
          rtcErrorParams.extraCode = ErrorCodeDictionary.AUDIO_ENCODE_FAILED;
          rtcErrorParams.code = ErrorCode.OPERATION_FAILED;
          break;
        case CoreErrorCode.UNKNOWN:
          break;
        // case CoreErrorCode.PLAY_NOT_ALLOWED:
        // case CoreErrorCode.SCHEDULE_FAILED:
        // case CoreErrorCode.API_CALL_TIMEOUT:
        // case CoreErrorCode.REMOTE_STREAM_NOT_EXIST:
        // case CoreErrorCode.DOWNLINK_RECONNECTION_FAILED:
        // case CoreErrorCode.UPLINK_RECONNECTION_FAILED:
        // case CoreErrorCode.SIGNAL_CHANNEL_RECONNECTION_FAILED:
        // case CoreErrorCode.ICE_TRANSPORT_ERROR:
        // case CoreErrorCode.SIGNAL_CHANNEL_SETUP_FAILED:
        default:
          rtcErrorParams.code = ErrorCode.OPERATION_FAILED;
      }
      err = new RtcError(rtcErrorParams);
      if (stack) err.stack += stack.substr(stack.indexOf('\n'));
    } else if (originError instanceof RtcError) {
      return originError;
    } else {
      err = new RtcError({
        code: ErrorCode.UNKNOWN_ERROR,
        fnName,
        originError
      });
    }
    return err as RtcError;
  }
}

function convertDeviceError(errorName: string) {
  let errorCode: number;
  switch (errorName) {
    case 'NotFoundError':
      errorCode = ErrorCodeDictionary.DEVICE_NOT_FOUND_ERROR;
      break;
    case 'NotAllowedError':
      errorCode = ErrorCodeDictionary.DEVICE_NOT_ALLOWED_ERROR;
      break;
    case 'NotReadableError':
      errorCode = ErrorCodeDictionary.DEVICE_NOT_READABLE_ERROR;
      break;
    case 'OverconstrainedError':
      errorCode = ErrorCodeDictionary.DEVICE_OVERCONSTRAINED_ERROR;
      break;
    case 'InvalidStateError':
      errorCode = ErrorCodeDictionary.DEVICE_INVALID_STATE_ERROR;
      break;
    case 'SecurityError':
      errorCode = ErrorCodeDictionary.DEVICE_SECURITY_ERROR;
      break;
    case 'AbortError':
      errorCode = ErrorCodeDictionary.DEVICE_ABORT_ERROR;
      break;
    default:
      errorCode = 5300;
  }
  return errorCode;
}
export default RtcError;
